<template>
    <div>
        <app-layout>
            <template v-slot:header>
                <Header :title="posting ? posting.company_name : ''" :isFilter="false" />
            </template>
            <template v-slot:header-mobile>
                <HeaderMobile :title="posting ? posting.company_name : ''" :isFilter="false" />
            </template>
            <div v-if="posting">
                <b-button variant="outline-secondary" class="mb-3" @click="$router.push('/coop/student')">
                    <i class="ri-arrow-left-line"></i> {{ $t('back') }}
                </b-button>

                <h6 class="mb-3">{{ posting.position }}</h6>

                <fieldset class="border p-3 mb-3 rounded">
                    <legend class="w-auto px-2 font-weight-bold h6">{{ $t('coop_posting_info') }}</legend>
                    <div>{{ $t('coop_location') }}: {{ posting.location_type_name }}</div>
                    <div>{{ $t('coop_quota') }}: {{ posting.quota }}</div>
                    <div>{{ $t('coop_last_application_date') }}: {{ posting.end_date }}</div>
                </fieldset>

                <fieldset class="border p-3 mb-3 rounded" v-if="posting.company_intro">
                    <legend class="w-auto px-2 font-weight-bold h6">{{ $t('coop_company_intro') }}</legend>
                    <p class="mb-0" v-html="posting.company_intro"></p>
                </fieldset>

                <fieldset class="border p-3 mb-3 rounded" v-if="posting.requirements">
                    <legend class="w-auto px-2 font-weight-bold h6">{{ $t('coop_required_qualifications') }}</legend>
                    <p class="mb-0" v-html="posting.requirements"></p>
                </fieldset>

                <fieldset class="border p-3 mb-3 rounded" v-if="posting.benefits">
                    <legend class="w-auto px-2 font-weight-bold h6">{{ $t('coop_provided_benefits') }}</legend>
                    <p class="mb-0" v-html="posting.benefits"></p>
                </fieldset>

                <ValidationObserver ref="formValidate" v-slot="{ handleSubmit }">
                    <b-form @submit.prevent="handleSubmit(submitApplication)">
                        <fieldset class="border p-3 mb-3 rounded">
                            <legend class="w-auto px-2 d-flex align-items-center">
                                <span class="font-weight-bold h6 mb-0">{{ $t('coop_application') }}</span>
                                <b-badge class="ml-2" :variant="isApplicationSent ? 'success' : 'secondary'">
                                    {{ $t('application_status') }}: {{ applicationStatusLabel }}
                                </b-badge>
                            </legend>
                            <ValidationProvider name="available_days" rules="required" v-slot="{ errors }">
                                <b-form-group :label="$t('coop_available_working_days')">
                                    <multi-parameter-selectbox code="coop_internship_application.available_days"
                                                               v-model="applicationForm.available_days"
                                                               sort="rank"
                                                               :disabled="isApplicationSent"/>
                                    <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" class="d-block mb-2"/>
                                </b-form-group>
                            </ValidationProvider>
                            <ValidationProvider name="kvkk_approved" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
                                <b-form-checkbox v-model="applicationForm.kvkk_approved" :disabled="isApplicationSent">
                                    {{ $t('coop_kvkk_approval_text') }}
                                </b-form-checkbox>
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" class="d-block mb-2"/>
                            </ValidationProvider>
                            <ValidationProvider name="terms_approved" :rules="{ required: { allowFalse: false } }" v-slot="{ errors }">
                                <b-form-checkbox v-model="applicationForm.terms_approved" :disabled="isApplicationSent">
                                    {{ $t('coop_terms_approval_text') }}
                                </b-form-checkbox>
                                <b-form-invalid-feedback v-if="errors[0]" v-html="errors[0]" class="d-block mb-2"/>
                            </ValidationProvider>
                            <div class="d-flex align-items-center mt-3">
                                <b-dropdown :text="selectedCvName || $t('coop_select_cv')" variant="outline-secondary" class="mr-2" :disabled="isApplicationSent">
                                    <b-dropdown-item v-for="cv in myCvs" :key="cv.id" @click="selectedCvId = cv.id">
                                        {{ cv.name || cv.title || cv.file_name }}
                                    </b-dropdown-item>
                                </b-dropdown>
                                <b-button variant="primary" type="submit" :disabled="isApplicationSent">
                                    {{ $t('coop_submit_application') }}
                                </b-button>
                            </div>
                        </fieldset>
                    </b-form>
                </ValidationObserver>
            </div>
        </app-layout>
    </div>
</template>
<script>
    //Template
    import AppLayout from '@/layouts/AppLayout';
    import Header from '@/layouts/AppLayout/Header';
    import HeaderMobile from '@/layouts/AppLayout/HeaderMobile';

    //Component
    import MultiParameterSelectbox from '@/components/interactive-fields/MultiParameterSelectbox.vue';

    //Service
    import CoopAdService from '@/services/CoopAdService';
    import CoopApplicationService from '@/services/CoopApplicationService';
    import CoopCvService from '@/services/CoopCvService';

    export default {
        components: {
            AppLayout,
            Header,
            HeaderMobile,
            MultiParameterSelectbox
        },
        metaInfo() {
            return {
                title: this.posting ? (this.posting.company_name || this.posting.position) : this.$t('coop_posting_info')
            }
        },
        data() {
            return {
                posting: null,
                myCvs: [],
                selectedCvId: null,
                applicationForm: {
                    available_days: [],
                    kvkk_approved: false,
                    terms_approved: false
                }
            };
        },
        computed: {
            isApplicationSent() {
                return this.posting && (this.posting.application_status === 'sent' || this.posting.has_applied);
            },
            applicationStatusLabel() {
                return this.isApplicationSent ? this.$t('sent') : this.$t('coop_application_draft');
            },
            selectedCvName() {
                const cv = this.myCvs.find((item) => item.id === this.selectedCvId);
                return cv ? (cv.name || cv.title || cv.file_name) : null;
            }
        },
        methods: {
            getPosting() {
                CoopAdService.get(this.$route.params.id).then((response) => {
                    this.posting = response.data.data || response.data;
                });
            },
            async submitApplication() {
                const isValid = await this.$refs.formValidate.validate();
                if (!isValid) return;

                if (!this.selectedCvId) {
                    this.$toast.error(this.$t('coop_please_select_cv'));
                    return;
                }

                const days = Array.isArray(this.applicationForm.available_days)
                    ? this.applicationForm.available_days
                    : (this.applicationForm.available_days ? [this.applicationForm.available_days] : []);

                const payload = {
                    cv_id: this.selectedCvId,
                    available_days: days,
                    kvkk_approved: this.applicationForm.kvkk_approved,
                    coop_terms_approved: this.applicationForm.terms_approved
                };

                CoopApplicationService.apply(this.posting.id, payload)
                    .then((response) => {
                        this.$toast.success(this.$t('api.' + response.data.message));
                        this.$router.push('/coop/student');
                    })
                    .catch((error) => {
                        this.showErrors(error, this.$refs.formValidate);
                    });
            }
        },
        created() {
            this.getPosting();
            CoopCvService.getAll().then((response) => {
                const items = response.data?.data || response.data || [];
                this.myCvs = items;
            });
        }
    }
</script>
